import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Drawer,
  Menu,
  MenuItem,
  Collapse,
} from "@mui/material";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import logo from "../assets/images/logoElecnor.webp";
import { profiles, ADMIN_SISTEMA } from "../contexts/constants/profiles";
import { logout } from "../api/userApi";
import CountrySelector from "./CountrySelector";
import { clearCountryStorage } from "../utils/countryStorage";

/**
 * Indica si una ruta del menú corresponde a la ubicación actual.
 *
 * No basta con comparar por prefijo: rutas como /subcontratos y /subcontratos-calendario
 * comparten inicio, y un startsWith simple marcaba las dos entradas del menú a la vez.
 * Se exige coincidencia exacta o que el prefijo termine en un separador de segmento, con
 * lo que /reporte-diario sigue marcándose al estar en /reporte-diario/editar/5.
 */
const isPathActive = (pathname: string, path: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

/**
 * Entrada del menú Informes tras la cual se inserta el submenú "Calendario de Recursos".
 * La posición la define el documento de reglas de negocio: va entre Programación Semanal
 * y Cronograma de Planificación, no al final de la lista.
 */
const CALENDAR_MENU_AFTER_PATH = "/programacion-semanal";

/**
 * Entrada del menú Informes tras la cual se inserta el submenú "Resumen".
 * Los dos resúmenes (semanal y mensual) se agrupan igual que los calendarios, y
 * el grupo ocupa el lugar donde antes estaba la entrada única "Resumen": justo
 * después de RAS.
 */
const SUMMARY_MENU_AFTER_PATH = "/RAS";

const Navbar: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [recursosAnchorEl, setRecursosAnchorEl] = useState<null | HTMLElement>(null);
  const [isInformesOpen, setIsInformesOpen] = useState(false);
  const [isRecursosOpen, setIsRecursosOpen] = useState(false);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<null | HTMLElement>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [summaryAnchorEl, setSummaryAnchorEl] = useState<null | HTMLElement>(null);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const open = Boolean(anchorEl);
  const recursosOpen = Boolean(recursosAnchorEl);
  const calendarOpen = Boolean(calendarAnchorEl);
  const summaryOpen = Boolean(summaryAnchorEl);

  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => isPathActive(location.pathname, path);

  const getRole = () => {
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr || userStr === "null" || userStr === "undefined") {
          return "";
        }
        const user = JSON.parse(userStr);
        return user?.rols?.description || "";
      } catch (error) {
        console.error("Error parseando el usuario desde localStorage", error);
        return "";
      }
  };

  const userRole = getRole();

  const sections = [
    { path: "/actividades", name: "Actividades", roles: [profiles[2].name] },
    { path: "/proyectos", name: "Proyectos", roles: [profiles[0].name, profiles[2].name] },
    { path: "/usuarios", name: "Usuarios", roles: [profiles[2].name] },
    { path: "/paises", name: "Países", roles: [profiles[5].name] },
    { path: "/reporte-diario", name: "Reporte Diario", roles: [profiles[0].name, profiles[1].name, profiles[2].name] },
  ].filter((section) => userRole === ADMIN_SISTEMA || !section.roles || section.roles.includes(userRole));

  // Los mantenedores de recursos se agrupan bajo un unico menu "Recursos". Las rutas se
  // mantienen tal como estaban (/personal, /equipos) para no romper enlaces guardados.
  const recursosOptions = [
    { path: "/personal", name: "Personal", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[4].name] },
    { path: "/equipos", name: "Equipos", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[4].name] },
    { path: "/subcontratos", name: "Subcontratos", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name, profiles[4].name] },
  ].filter((option) => userRole === ADMIN_SISTEMA || !option.roles || option.roles.includes(userRole));

  if (location.pathname === "/" || location.pathname === "/seleccionar-pais") {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    clearCountryStorage();
    navigate("/");
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
      return;
    }
    setIsDrawerOpen(open);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleInformesMenu = () => {
    setIsInformesOpen(!isInformesOpen);
  };

  const handleRecursosMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRecursosAnchorEl(event.currentTarget);
  };

  const handleRecursosMenuClose = () => {
    setRecursosAnchorEl(null);
  };

  const toggleRecursosMenu = () => {
    setIsRecursosOpen(!isRecursosOpen);
  };

  const handleCalendarMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  // Al elegir un calendario se cierran ambos niveles del menu.
  const handleCalendarMenuClose = () => {
    setCalendarAnchorEl(null);
  };

  const handleCalendarOptionClick = () => {
    setCalendarAnchorEl(null);
    handleMenuClose();
  };

  const toggleCalendarMenu = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  const handleSummaryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSummaryAnchorEl(event.currentTarget);
  };

  // Al elegir un resumen se cierran ambos niveles del menu.
  const handleSummaryMenuClose = () => {
    setSummaryAnchorEl(null);
  };

  const handleSummaryOptionClick = () => {
    setSummaryAnchorEl(null);
    handleMenuClose();
  };

  const toggleSummaryMenu = () => {
    setIsSummaryOpen(!isSummaryOpen);
  };

  const informesOptions = [
    { path: "/control-produccion", name: "Control de Producción", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name] },
    { path: "/RAS", name: "RAS", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name] },
    { path: "/consolidado-rendimiento", name: "Consolidado de Rendimientos", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name] },
    { path: "/consolidado-improductividad", name: "Consolidado de Improductividad", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name] },
    { path: "/programacion-semanal", name: "Programación Semanal", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name] },
    { path: "/espacio-tiempo", name: "Diagrama Espacio-Tiempo", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name] },
    { path: "/cronograma-planificacion", name: "Cronograma de Planificación" },
    { path: "/informes-powerbi", name: "Informes Power BI" }
  ];

  // Los dos resúmenes se agrupan en un submenu "Resumen" dentro de Informes.
  // La ruta /resumen se conserva para el semanal, para no romper enlaces guardados;
  // solo cambia la etiqueta, de "Resumen" a "Resumen Semanal".
  // Sin filtro de rol a propósito: informesOptions tampoco lo aplica, así que filtrar acá
  // le quitaría "Resumen" a perfiles que hoy sí lo ven (p. ej. Administrador de Sistema).
  const resumenOptions = [
    { path: "/resumen", name: "Resumen Semanal" },
    { path: "/resumen-mensual", name: "Resumen Mensual" },
  ];

  // Los tres calendarios se agrupan en un submenu "Calendario de Recursos" dentro de
  // Informes. Las rutas de los calendarios existentes se conservan sin cambios.
  const calendarOptions = [
    { path: "/personal-calendario", name: "Calendario de Personal", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name] },
    { path: "/equipment-calendar", name: "Calendario de Equipos", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name] },
    { path: "/subcontratos-calendario", name: "Calendario de Subcontratos", roles: [profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name] },
  ].filter((option) => !option.roles || option.roles.includes(userRole));

  const drawerList = (
    <Box sx={{ width: 250, height: "100vh", overflowY: "auto", display: "flex", flexDirection: "column" }} role="presentation">
      {/* Selector de país (mobile) */}
      <Box sx={{ p: 2, pb: 1 }}>
        <CountrySelector />
      </Box>
      <List sx={{ flexGrow: 1 }}>
        {/* Submenú "Informes" en Drawer */}
        <ListItem disablePadding sx={{ bgcolor: isInformesOpen ? "#f0f0f0" : "inherit", borderRadius: "5px", marginBottom: "8px" }}>
          <ListItemButton onClick={toggleInformesMenu}>
            <ListItemText primary="Informes" sx={{ fontWeight: "bold", color: isInformesOpen ? "#0056b3" : "inherit" }} />
            {isInformesOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={isInformesOpen} timeout="auto" unmountOnExit sx={{ bgcolor: "#e7f3ff", paddingLeft: "16px", borderLeft: "4px solid #0056b3", borderRadius: "4px" }}>
          <List component="div" disablePadding>
            {informesOptions.flatMap((option) => {
              const item = (
                <ListItem key={option.path} disablePadding>
                  <ListItemButton
                    component={NavLink}
                    to={option.path}
                    onClick={() => setIsDrawerOpen(false)}
                    selected={isActive(option.path)}
                    sx={{
                      "&.Mui-selected": { bgcolor: "#d0e7ff", borderLeft: "4px solid #0056b3" },
                      "&:hover": { bgcolor: "#d0e7ff" },
                    }}
                  >
                    <ListItemText primary={option.name} />
                  </ListItemButton>
                </ListItem>
              );

              // Submenú anidado de resúmenes, en el lugar donde antes iba la entrada "Resumen".
              if (option.path === SUMMARY_MENU_AFTER_PATH && resumenOptions.length > 0) {
                return [
                  item,
                  <ListItem key="resumen" disablePadding>
                    <ListItemButton onClick={toggleSummaryMenu}>
                      <ListItemText primary="Resumen" />
                      {isSummaryOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>,
                  <Collapse key="resumen-items" in={isSummaryOpen} timeout="auto" unmountOnExit sx={{ paddingLeft: "16px" }}>
                    <List component="div" disablePadding>
                      {resumenOptions.map((resumenOption) => (
                        <ListItem key={resumenOption.path} disablePadding>
                          <ListItemButton
                            component={NavLink}
                            to={resumenOption.path}
                            onClick={() => setIsDrawerOpen(false)}
                            selected={isActive(resumenOption.path)}
                            sx={{
                              "&.Mui-selected": { bgcolor: "#d0e7ff", borderLeft: "4px solid #0056b3" },
                              "&:hover": { bgcolor: "#d0e7ff" },
                            }}
                          >
                            <ListItemText primary={resumenOption.name} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                ];
              }

              if (option.path !== CALENDAR_MENU_AFTER_PATH || calendarOptions.length === 0) {
                return [item];
              }

              // Submenú anidado de calendarios, insertado en la posición que define el documento.
              return [
                item,
                <ListItem key="calendario-recursos" disablePadding>
                  <ListItemButton onClick={toggleCalendarMenu}>
                    <ListItemText primary="Calendario de Recursos" />
                    {isCalendarOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>,
                <Collapse key="calendario-recursos-items" in={isCalendarOpen} timeout="auto" unmountOnExit sx={{ paddingLeft: "16px" }}>
                  <List component="div" disablePadding>
                    {calendarOptions.map((calendarOption) => (
                      <ListItem key={calendarOption.path} disablePadding>
                        <ListItemButton
                          component={NavLink}
                          to={calendarOption.path}
                          onClick={() => setIsDrawerOpen(false)}
                          selected={isActive(calendarOption.path)}
                          sx={{
                            "&.Mui-selected": { bgcolor: "#d0e7ff", borderLeft: "4px solid #0056b3" },
                            "&:hover": { bgcolor: "#d0e7ff" },
                          }}
                        >
                          <ListItemText primary={calendarOption.name} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              ];
            })}
          </List>
        </Collapse>

        {/* Submenú "Recursos" en Drawer */}
        {recursosOptions.length > 0 && (
          <>
            <ListItem disablePadding sx={{ bgcolor: isRecursosOpen ? "#f0f0f0" : "inherit", borderRadius: "5px", marginBottom: "8px" }}>
              <ListItemButton onClick={toggleRecursosMenu}>
                <ListItemText primary="Recursos" sx={{ fontWeight: "bold", color: isRecursosOpen ? "#0056b3" : "inherit" }} />
                {isRecursosOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={isRecursosOpen} timeout="auto" unmountOnExit sx={{ bgcolor: "#e7f3ff", paddingLeft: "16px", borderLeft: "4px solid #0056b3", borderRadius: "4px" }}>
              <List component="div" disablePadding>
                {recursosOptions.map((option) => (
                  <ListItem key={option.path} disablePadding>
                    <ListItemButton
                      component={NavLink}
                      to={option.path}
                      onClick={() => setIsDrawerOpen(false)}
                      selected={isActive(option.path)}
                      sx={{
                        "&.Mui-selected": { bgcolor: "#d0e7ff", borderLeft: "4px solid #0056b3" },
                        "&:hover": { bgcolor: "#d0e7ff" },
                      }}
                    >
                      <ListItemText primary={option.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {sections.map((section) => (
          <ListItem key={section.path} disablePadding>
            <ListItemButton
              component={NavLink}
              to={section.path}
              onClick={() => setIsDrawerOpen(false)}
              selected={isActive(section.path)}
              sx={{
                "&.Mui-selected": { bgcolor: "#d0e7ff", borderLeft: "4px solid #0056b3" },
                "&:hover": { bgcolor: "#d0e7ff" },
              }}
            >
              <ListItemText primary={section.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
  
      {/* Botón de cerrar sesión en el fondo del Drawer */}
      <Box sx={{ padding: 2 }}>
        <Button onClick={handleLogout} variant="contained" color="primary" fullWidth>
          Cerrar sesión
        </Button>
      </Box>
    </Box>
  );
  

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: "#ffffff", color: "#000000", boxShadow: "none" }}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img src={logo} alt="elecnor logo" style={{ width: "120px", height: "auto" }} />
          </Box>

          {/* Menú mobile */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Menú desktop */}
          {/* Navbar desktop */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
          >
            {/* Botón "Informes" con subrayado activo si la ruta actual pertenece a informes */}
            <Button
              color="inherit"
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                borderBottom: [...informesOptions, ...resumenOptions, ...calendarOptions].some((option) =>
                  isActive(option.path)
                )
                  ? "2px solid #0056b3"
                  : "2px solid transparent",
                "&:hover": { borderBottom: "2px solid #0056b3" },
                marginLeft: 2,
              }}
              onClick={handleMenuOpen}
            >
              Informes
            </Button>

            {/* Botón "Recursos": agrupa los mantenedores de Personal, Equipos y Subcontratos */}
            {recursosOptions.length > 0 && (
              <Button
                color="inherit"
                sx={{
                  textTransform: "none",
                  fontWeight: "bold",
                  borderBottom: recursosOptions.some((option) =>
                    isActive(option.path)
                  )
                    ? "2px solid #0056b3"
                    : "2px solid transparent",
                  "&:hover": { borderBottom: "2px solid #0056b3" },
                  marginLeft: 2,
                }}
                onClick={handleRecursosMenuOpen}
              >
                Recursos
              </Button>
            )}

            {sections.map((section) => (
              <Button
                key={section.path}
                component={NavLink}
                to={section.path}
                color="inherit"
                sx={{
                  textTransform: "none",
                  fontWeight: "bold",
                  borderBottom: isActive(section.path)
                    ? "2px solid #0056b3"
                    : "2px solid transparent",
                  "&.active": { borderBottom: "2px solid #0056b3" },
                  marginLeft: 2,
                }}
              >
                {section.name}
              </Button>
            ))}

            {/* Submenú para "Informes" */}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              {/* Se usa flatMap y no un Fragment para que MenuList reciba los MenuItem como
                  hijos directos y la navegación por teclado siga funcionando. */}
              {informesOptions.flatMap((option) => {
                const item = (
                  <MenuItem
                    key={option.path}
                    component={NavLink}
                    to={option.path}
                    onClick={handleMenuClose}
                  >
                    {option.name}
                  </MenuItem>
                );

                // Entrada que despliega el submenú anidado de resúmenes.
                if (option.path === SUMMARY_MENU_AFTER_PATH && resumenOptions.length > 0) {
                  return [
                    item,
                    <MenuItem
                      key="resumen"
                      onClick={handleSummaryMenuOpen}
                      sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
                    >
                      Resumen
                      <ExpandMore fontSize="small" sx={{ transform: "rotate(-90deg)" }} />
                    </MenuItem>
                  ];
                }

                if (option.path !== CALENDAR_MENU_AFTER_PATH || calendarOptions.length === 0) {
                  return [item];
                }

                // Entrada que despliega el submenú anidado de calendarios.
                return [
                  item,
                  <MenuItem
                    key="calendario-recursos"
                    onClick={handleCalendarMenuOpen}
                    sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
                  >
                    Calendario de Recursos
                    <ExpandMore fontSize="small" sx={{ transform: "rotate(-90deg)" }} />
                  </MenuItem>
                ];
              })}
            </Menu>

            {/* Submenú anidado: Resumen Semanal / Resumen Mensual */}
            <Menu
              anchorEl={summaryAnchorEl}
              open={summaryOpen}
              onClose={handleSummaryMenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {resumenOptions.map((option) => (
                <MenuItem
                  key={option.path}
                  component={NavLink}
                  to={option.path}
                  onClick={handleSummaryOptionClick}
                >
                  {option.name}
                </MenuItem>
              ))}
            </Menu>

            {/* Submenú anidado: los tres calendarios */}
            <Menu
              anchorEl={calendarAnchorEl}
              open={calendarOpen}
              onClose={handleCalendarMenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {calendarOptions.map((option) => (
                <MenuItem
                  key={option.path}
                  component={NavLink}
                  to={option.path}
                  onClick={handleCalendarOptionClick}
                >
                  {option.name}
                </MenuItem>
              ))}
            </Menu>

            {/* Submenú para "Recursos" */}
            <Menu
              anchorEl={recursosAnchorEl}
              open={recursosOpen}
              onClose={handleRecursosMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              {recursosOptions.map((option) => (
                <MenuItem
                  key={option.path}
                  component={NavLink}
                  to={option.path}
                  onClick={handleRecursosMenuClose}
                >
                  {option.name}
                </MenuItem>
              ))}
            </Menu>
          </Box>


          {/* Selector de país (desktop) */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", marginLeft: "20px" }}>
            <CountrySelector />
          </Box>

          <Button onClick={handleLogout} variant="contained" color="primary" sx={{ display: { xs: "none", md: "block" }, marginLeft: "20px" }}>
            Cerrar sesión
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={isDrawerOpen} onClose={toggleDrawer(false)}>
        {drawerList}
      </Drawer>
    </>
  );
};

export default Navbar;
